import Team from '@/components/Team/Team'
import { useTheme } from '@/contexts/ThemeContext'
import { useTemplateAnimation } from '@/hooks/useTemplateAnimation'
import { getHomeCompetitor, getAwayCompetitor } from '@/services/jsonDataService'
import { hexToRgba } from '@/utils/colorUtils'
import { POSITION_CLASSES, POSITION_ANIMATIONS } from '@/utils/scorebugPosition'

import { oversText, teamVisibility } from './cricketManualDisplay'

import './CricketManualBug.css'

import type {
  Competitor,
  CricketManualEventState,
  CricketManualScore,
  JsonMatchData,
  ScoreBugPosition,
} from '@/services/jsonDataService'
import type { MatchData } from '@/types'
import type { CSSProperties, FC } from 'react'

export interface CricketManualBugProps {
  matchData?: MatchData
  jsonData: JsonMatchData | null
  isVisible?: boolean
  scorebugPosition?: ScoreBugPosition
}

const MULTIPLIERS = {
  scoreFontSize: 1.0,
  oversFontSize: 0.55,
  oversBoxSize: 1.2,
  /** Wider box for the "12.3 / 20" current-plus-available form. */
  oversBoxSizeWide: 2.4,
  scoreBoxSize: 3,
  battingDotSize: 0.2,
  footerHeight: 0.8,
  footerLogoHeight: 0.4,
}

interface CricketManualRowProps {
  competitor?: Competitor
  isHome: boolean
  score?: CricketManualScore
  isBatting: boolean
  /** Pre-composed overs label; empty hides the overs box. */
  oversLabel: string
  baseHeight: string
}

const CricketManualRow: FC<CricketManualRowProps> = ({
  competitor,
  isHome,
  score,
  isBatting,
  oversLabel,
  baseHeight,
}) => {
  const oversBoxSize = oversLabel.includes('/') ? MULTIPLIERS.oversBoxSizeWide : MULTIPLIERS.oversBoxSize
  return (
    <div className="cricket-bug-team-row">
      <Team data={competitor} isHome={isHome} style={{ flex: 1, minWidth: 0 }} />
      {isBatting && (
        <span
          className="cricket-bug-batting-dot"
          style={{
            width: `calc(${baseHeight} * ${MULTIPLIERS.battingDotSize})`,
            height: `calc(${baseHeight} * ${MULTIPLIERS.battingDotSize})`,
          }}
          aria-label="Batting"
        />
      )}
      <div className="cricket-bug-score-box" style={{ width: `calc(${baseHeight} * ${MULTIPLIERS.scoreBoxSize})` }}>
        <span
          className="cricket-bug-score-value"
          style={{ fontSize: `calc(${baseHeight} * ${MULTIPLIERS.scoreFontSize})` }}
        >
          {score?.score ?? ''}
        </span>
      </div>
      {oversLabel !== '' && (
        <div className="cricket-bug-overs-box" style={{ width: `calc(${baseHeight} * ${oversBoxSize})` }}>
          <span
            className="cricket-bug-overs-value"
            style={{ fontSize: `calc(${baseHeight} * ${MULTIPLIERS.oversFontSize})` }}
          >
            {oversLabel}
          </span>
        </div>
      )}
    </div>
  )
}

const CricketManualBug: FC<CricketManualBugProps> = ({
  jsonData,
  isVisible = true,
  scorebugPosition = 'bottom-left',
}) => {
  const theme = useTheme()
  const { in: animationType } = POSITION_ANIMATIONS[scorebugPosition]
  const animationRef = useTemplateAnimation(isVisible, { animationType, duration: 0.5 })

  const homeCompetitor = getHomeCompetitor(jsonData)
  const awayCompetitor = getAwayCompetitor(jsonData)

  const homeScore = homeCompetitor?.score as CricketManualScore | undefined
  const awayScore = awayCompetitor?.score as CricketManualScore | undefined

  const eventState = jsonData?.eventState as CricketManualEventState | undefined
  const batting = eventState?.batting ?? 1
  const teams = teamVisibility(eventState)

  const positionClass = POSITION_CLASSES[scorebugPosition]
  const bodyStyle: CSSProperties = { background: hexToRgba(theme.colors.primary, 0.95) }
  const footerStyle: CSSProperties = {
    background: hexToRgba(theme.colors.secondary, 0.95),
    height: `calc(${theme.baseHeight} * ${MULTIPLIERS.footerHeight})`,
  }

  return (
    <div className={`cricket-bug-content-wrapper ${positionClass}`}>
      <div ref={animationRef} className="cricket-bug-panel" data-scorebug-panel>
        <div className="cricket-bug-body" style={bodyStyle}>
          <div className="cricket-bug-rows">
            {teams.home && (
              <CricketManualRow
                competitor={homeCompetitor}
                isHome
                score={homeScore}
                isBatting={batting === 1}
                oversLabel={oversText(homeScore, eventState)}
                baseHeight={theme.baseHeight}
              />
            )}
            {teams.away && (
              <CricketManualRow
                competitor={awayCompetitor}
                isHome={false}
                score={awayScore}
                isBatting={batting === 2}
                oversLabel={oversText(awayScore, eventState)}
                baseHeight={theme.baseHeight}
              />
            )}
          </div>
        </div>

        <div className="cricket-bug-footer" style={footerStyle}>
          <img
            src="/assets/frogbox-logo-mono.png"
            alt="FrogBox"
            className="cricket-bug-branding-logo"
            style={{ height: `calc(${theme.baseHeight} * ${MULTIPLIERS.footerLogoHeight})` }}
          />
        </div>
      </div>
    </div>
  )
}

export default CricketManualBug
